import React, { useEffect, useState } from "react";
import CounterViewModel from "../viewmodels/CounterViewModel";
import GenerateQuote from "../viewmodels/GenerateQuote";
import ImageViewModel from "../viewmodels/ImageViewModel";
import styled, { keyframes } from "styled-components";

const HomeView = () => {
  const [imageUrl, setImageUrl] = useState("");
  const [spinnerFinished, setSpinnerFinished] = useState(false);
  const [imageFetched, setImageFetched] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [dots, setDots] = useState(".");

  // Spinner: Show for 2 seconds
  useEffect(() => {
    const spinnerTimer = setTimeout(() => {
      setSpinnerFinished(true);
    }, 1500);

    const dotsInterval = setInterval(() => {
      setDots((prevDots) => (prevDots.length === 3 ? "." : prevDots + "."));
    }, 500);

    setLoadingText(GenerateQuote());

    return () => {
      clearTimeout(spinnerTimer);
      clearInterval(dotsInterval);
    };
  }, []);

  // Fetch the current image
  useEffect(() => {
    let isCancelled = false;
    const fetchCurrentImage = async () => {
      try {
        const counter = await CounterViewModel.getCounter();
        let { url } = await ImageViewModel.getSignedImageUrl(counter);
        
        if (isCancelled) return;

        // Preload to verify the exact same 1-month disk cache expiration risk
        let imageIsValid = await new Promise((resolve) => {
          const img = new window.Image();
          img.src = url;
          if (img.complete) resolve(true);
          else {
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
          }
        });

        if (!imageIsValid) {
          console.warn(`URL for image ${counter} expired or failed. Self-healing and refetching fresh from AWS...`);
          ImageViewModel.invalidate(counter);
          // Force a backend cache bypass just like GalleryView
          const freshFetch = await ImageViewModel.getSignedImageUrl(counter, true);
          url = freshFetch.url;

          await new Promise((resolve) => {
            const retryImg = new window.Image();
            retryImg.src = url;
            if (retryImg.complete) resolve();
            else {
              retryImg.onload = resolve;
              retryImg.onerror = resolve;
            }
          });
        }

        if (isCancelled) return;
        setImageUrl(url);
        setImageFetched(true);
      } catch (error) {
        console.error("Error fetching the current image:", error);
        if (!isCancelled) setImageFetched(true);
      }
    };

    fetchCurrentImage();
    
    return () => {
      isCancelled = true;
    };
  }, []);

  // The loading state is true until both the spinner timer and the image fetch are complete
  const loading = !(spinnerFinished && imageFetched);

  return (
    <Container>
      {loading ? (
        <LoadingContainer>
          <LoadingText>
            {loadingText}
            {dots}
          </LoadingText>
          <LoadingSpinner />
        </LoadingContainer>
      ) : (
        imageUrl && (
          <Image className="fade-in" src={imageUrl} alt="Current Image" />
        )
      )}
    </Container>
  );
};

export default HomeView;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 60px);
  padding: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const LoadingSpinner = styled.div`
  border: 8px solid #000; /* Black */
  border-top: 8px solid #f00; /* Red */
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: ${spin} 1s linear infinite;
  margin-top: 20px;
`;

const LoadingText = styled.div`
  font-family: "Heiti SC", sans-serif;
  font-size: 1.5vw;
  color: red;
  margin-bottom: 10px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 4vw;
  }

  @media (max-width: 480px) {
    font-size: 6vw;
  }
`;

const Image = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 2s ease forwards;
  opacity: 0;

  &.fade-in {
    opacity: 1;
  }
`;
